import { useMutation } from "@tanstack/react-query"
import type {
  UseMutationOptions,
  UseMutationResult,
} from "@tanstack/react-query"

import client from "~/backend/axiosClient"

import type {
  ImportProductsMutationRequest,
  ImportProductsMutationResponse,
} from "../types/ImportProducts"

type ImportProductsClient = typeof client<
  ImportProductsMutationResponse,
  Error,
  ImportProductsMutationRequest
>
type ImportProducts = {
  data: ImportProductsMutationResponse
  error: Error
  request: ImportProductsMutationRequest
  pathParams: never
  queryParams: never
  headerParams: never
  response: Awaited<ReturnType<ImportProductsClient>>
  client: {
    parameters: Partial<Parameters<ImportProductsClient>[0]>
    return: Awaited<ReturnType<ImportProductsClient>>
  }
}
/**
 * @summary Import products
 * @link /v1/product/import-products
 */
export function useImportProducts(
  options: {
    mutation?: UseMutationOptions<
      ImportProducts["response"],
      ImportProducts["error"],
      ImportProducts["request"]
    >
    client?: ImportProducts["client"]["parameters"]
  } = {}
): UseMutationResult<
  ImportProducts["response"],
  ImportProducts["error"],
  ImportProducts["request"]
> {
  const { mutation: mutationOptions, client: clientOptions = {} } =
    options ?? {}
  return useMutation<
    ImportProducts["response"],
    ImportProducts["error"],
    ImportProducts["request"]
  >({
    mutationFn: async (data) => {
      const res = await client<
        ImportProducts["data"],
        ImportProducts["error"],
        ImportProducts["request"]
      >({
        method: "post",
        url: `/v1/product/import-products`,
        data,
        ...clientOptions,
      })
      return res
    },
    ...mutationOptions,
  })
}
